// components/GanttChart/MembersModal.js
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    padding: 16px;
  }
`;

const MemberItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #eee;
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: cover;
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-weight: 500;
`;

const MemberRole = styled.div`
  font-size: 12px;
  color: #666;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
`;

const EditButton = styled(ActionButton)`
  background-color: #FFC107;
  
  &:hover {
    background-color: #e0a800;
  }
`;

const DeleteButton = styled(ActionButton)`
  background-color: #dc3545;
  
  &:hover {
    background-color: #bd2130;
  }
`;

const AddMemberForm = styled.form`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  gap: 12px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const AddButton = styled.button`
  padding: 8px 16px;
  background-color: #4EA8DE;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #3b86b5;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ImageUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AvatarPreview = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #f3f4f6;
  cursor: pointer;
  border: 1px dashed #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CloseButton = styled.button`
  margin-top: 16px;
  padding: 8px 16px;
  background-color: #f1f1f1;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  align-self: flex-end;
  font-weight: 500;
  
  &:hover {
    background-color: #e1e1e1;
  }
`;

// 수정 모달용 스타일
const EditMemberModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const EditModalContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 16px;
`;

export default function MembersModal({ isOpen, onClose, members, onAddMember, onRemoveMember, onEditMember, chartType, chartId }) {
  const isTeam = chartType === 'team';
  const [newMemberName, setNewMemberName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  // 팀 차트: 차트의 실제 공유 멤버 풀
  const [teamPool, setTeamPool] = useState([]);
  const [poolLoading, setPoolLoading] = useState(false);
  useEffect(() => {
    if (!isOpen || !isTeam || !chartId) return;
    setPoolLoading(true);
    fetch(`/request/gantt-chart/${chartId}/shares`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setTeamPool(d.shares || []); })
      .catch((e) => console.error('팀원 조회 실패:', e))
      .finally(() => setPoolLoading(false));
  }, [isOpen, isTeam, chartId]);
  
  // 수정 모달 상태
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editMemberId, setEditMemberId] = useState(null);
  const [editMemberName, setEditMemberName] = useState('');
  const [editAvatarPreview, setEditAvatarPreview] = useState(null);
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const editFileInputRef = useRef(null);
  
  if (!isOpen) return null;
  
  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleEditAvatarClick = () => {
    if (editFileInputRef.current) {
      editFileInputRef.current.click();
    }
  };
  
  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newMemberName.trim()) {
      toast.error('담당자 이름을 입력해주세요.');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // 프로필 이미지 업로드 처리
      let avatarUrl = "/resource/defaultImg.png?height=40&width=40";
      
      if (avatarFile) {
        // FormData 생성 및 파일 추가
        const uploadFormData = new FormData();
        uploadFormData.append('files', avatarFile);
        
        try {
          // AWS 파일 서버에 업로드
          const uploadResponse = await fetch('https://file.skynet.re.kr/awsUpload/files', {
            method: 'POST',
            body: uploadFormData,
          });
          
          if (!uploadResponse.ok) {
            throw new Error(`업로드 실패: ${uploadResponse.status} ${uploadResponse.statusText}`);
          }
          
          const uploadData = await uploadResponse.json();
          
          if (uploadData.message === "Multiple files uploaded successfully" && 
              uploadData.files && 
              uploadData.files.length > 0) {
            
            avatarUrl = uploadData.files[0].url;
          } else {
            throw new Error("이미지 업로드 응답 형식이 예상과 다릅니다.");
          }
        } catch (uploadError) {
          console.error("이미지 업로드 오류:", uploadError);
          toast.error(`프로필 이미지 업로드 실패: ${uploadError.message}`);
        }
      }
      
      // 새 담당자 추가
      onAddMember({
        name: newMemberName,
        avatar: avatarUrl
      });
      
      // 폼 초기화
      setNewMemberName('');
      setAvatarPreview(null);
      setAvatarFile(null);
      
      toast.success('담당자가 추가되었습니다.');
    } catch (error) {
      console.error('담당자 추가 오류:', error);
      toast.error('담당자 추가 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
    }
  };
  
  const openEditModal = (member) => {
    setEditMemberId(member.id);
    setEditMemberName(member.name);
    setEditAvatarPreview(member.avatar);
    setEditAvatarFile(null);
    setIsEditModalOpen(true);
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editMemberName.trim()) {
      toast.error('담당자 이름을 입력해주세요.');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // 프로필 이미지 업로드 처리
      let avatarUrl = editAvatarPreview;
      
      if (editAvatarFile) {
        // FormData 생성 및 파일 추가
        const uploadFormData = new FormData();
        uploadFormData.append('files', editAvatarFile);
        
        try {
          // AWS 파일 서버에 업로드
          const uploadResponse = await fetch('https://file.skynet.re.kr/awsUpload/files', {
            method: 'POST',
            body: uploadFormData,
          });
          
          if (!uploadResponse.ok) {
            throw new Error(`업로드 실패: ${uploadResponse.status} ${uploadResponse.statusText}`);
          }
          
          const uploadData = await uploadResponse.json();
          
          if (uploadData.message === "Multiple files uploaded successfully" && 
              uploadData.files && 
              uploadData.files.length > 0) {
            
            avatarUrl = uploadData.files[0].url;
          } else {
            throw new Error("이미지 업로드 응답 형식이 예상과 다릅니다.");
          }
        } catch (uploadError) {
          console.error("이미지 업로드 오류:", uploadError);
          toast.error(`프로필 이미지 업로드 실패: ${uploadError.message}`);
        }
      }
      
      // 담당자 수정
      onEditMember(editMemberId, {
        name: editMemberName,
        avatar: avatarUrl
      });
      
      // 수정 모달 닫기
      setIsEditModalOpen(false);
      toast.success('담당자 정보가 수정되었습니다.');
    } catch (error) {
      console.error('담당자 수정 오류:', error);
      toast.error('담당자 수정 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
    }
  };
  
  // 팀원 풀에서 아직 담당자로 안 추가된 사람만 노출
  const alreadyAdded = new Set((members || []).map(m => String(m.id)));
  const availableTeamPool = teamPool.filter(u => !alreadyAdded.has(String(u.user_id)));

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <h2>프로젝트 담당자 관리</h2>

        {isTeam ? (
          <div style={{ marginBottom: 16, padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
            <div style={{ fontSize: 13, color: '#1e40af', fontWeight: 600, marginBottom: 8 }}>
              👥 팀 차트 — 초대된 멤버만 담당자로 추가할 수 있습니다
            </div>
            {poolLoading ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>팀원 불러오는 중…</div>
            ) : availableTeamPool.length === 0 ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>
                추가 가능한 팀원이 없어요. 헤더의 [초대] 버튼으로 먼저 팀원을 초대하세요.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {availableTeamPool.map(u => (
                  <div key={u.user_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: 8, background: 'white', borderRadius: 6, border: '1px solid #e5e7eb'
                  }}>
                    <img src={u.profile_image || '/resource/defaultImg.png'} alt={u.user_name}
                         style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {u.user_name}
                        {u.permission === 'owner' && (
                          <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>소유자</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{u.user_email || u.user_account}</div>
                    </div>
                    <AddButton
                      type="button"
                      onClick={() => {
                        onAddMember({
                          ssoUserId: u.user_id, // page.jsx 쪽에서 SSO ID 우선 사용하도록
                          name: u.user_name,
                          avatar: u.profile_image || '/resource/defaultImg.png',
                        });
                      }}
                    >+ 담당자</AddButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <AddMemberForm onSubmit={handleSubmit}>
            <ImageUploadContainer>
              <AvatarPreview onClick={handleAvatarClick}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="미리보기" />
                ) : (
                  <span>+</span>
                )}
              </AvatarPreview>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>프로필 이미지</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  클릭하여 이미지를 선택하세요
                </p>
              </div>
            </ImageUploadContainer>

            <FormRow>
              <Input
                type="text"
                placeholder="담당자 이름"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
              />
              <AddButton type="submit" disabled={isUploading}>
                {isUploading ? '추가 중...' : '추가'}
              </AddButton>
            </FormRow>
          </AddMemberForm>
        )}
        
        <div>
          {members.map(member => (
            <MemberItem key={member.id}>
              <Avatar src={member.avatar} alt={member.name} />
              <MemberInfo>
                <MemberName>{member.name}</MemberName>
                <MemberRole>{member.role || '팀원'}</MemberRole>
              </MemberInfo>
              <ButtonGroup>
                <EditButton onClick={() => openEditModal(member)}>
                  수정
                </EditButton>
                <DeleteButton onClick={() => onRemoveMember(member.id)}>
                  삭제
                </DeleteButton>
              </ButtonGroup>
            </MemberItem>
          ))}
        </div>
        
        <CloseButton onClick={onClose}>
          닫기
        </CloseButton>
      </ModalContent>
      
      {/* 담당자 수정 모달 */}
      {isEditModalOpen && (
        <EditMemberModal onClick={() => setIsEditModalOpen(false)}>
          <EditModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>담당자 정보 수정</ModalTitle>
            
            <AddMemberForm onSubmit={handleEditSubmit}>
              <ImageUploadContainer>
                <AvatarPreview onClick={handleEditAvatarClick}>
                  {editAvatarPreview ? (
                    <img src={editAvatarPreview} alt="미리보기" />
                  ) : (
                    <span>+</span>
                  )}
                </AvatarPreview>
                <input
                  type="file"
                  accept="image/*"
                  ref={editFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleEditFileChange}
                />
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>프로필 이미지</p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                    클릭하여 이미지를 변경하세요
                  </p>
                </div>
              </ImageUploadContainer>
              
              <FormRow>
                <Input
                  type="text"
                  placeholder="담당자 이름"
                  value={editMemberName}
                  onChange={(e) => setEditMemberName(e.target.value)}
                />
              </FormRow>
              
              <ButtonGroup style={{ justifyContent: 'flex-end', marginTop: '16px' }}>
                <button 
                  type="button" 
                  onClick={() => setIsEditModalOpen(false)}
                  style={{ 
                    padding: '8px 16px', 
                    background: '#f1f1f1', 
                    border: 'none',
                    borderRadius: '4px',
                    marginRight: '8px',
                    cursor: 'pointer' 
                  }}
                >
                  취소
                </button>
                <AddButton type="submit" disabled={isUploading}>
                  {isUploading ? '저장 중...' : '저장'}
                </AddButton>
              </ButtonGroup>
            </AddMemberForm>
          </EditModalContent>
        </EditMemberModal>
      )}
    </ModalOverlay>
  );
}